'use client';

import { makePath, matchPath } from '@lfg/core/lib';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import { AppBar, Box, Container, Drawer, IconButton, Stack, Toolbar, Typography } from '@mui/material';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import React, { cloneElement, useCallback, useEffect, useMemo, useState } from 'react';

import HexagonalButton from '@/components/bases/HexagonalButton';

const Header = () => {
  const path = usePathname();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);

    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  const openAdminPage = () => {
    window.open(process.env.NEXT_PUBLIC_ADMIN_URL, '_blank');
  };

  const navs = useMemo(
    () => [
      { label: 'Explore', href: '/explore', pattern: makePath('/explore') },
      { label: 'FAQ', href: '/faq', pattern: makePath('/faq') },
    ],
    [],
  );

  const renderNav = useCallback(
    (onMobileMenuClose?: () => void) => {
      return navs.map((nav, index) => {
        const isActive = matchPath(path, nav.pattern);

        return (
          <Link key={index} href={nav.href} style={{ textDecoration: 'none' }} onClick={onMobileMenuClose}>
            <Typography
              sx={{
                color: isActive ? '#FFFFFF' : '#FFFFFF59',
                fontWeight: 500,
                '&:hover': { color: '#FFFFFF' },
              }}>
              {nav.label}
            </Typography>
          </Link>
        );
      });
    },
    [navs, path],
  );

  return (
    <AppBar
      position='fixed'
      sx={{
        backgroundColor: scrolled ? 'rgba(255, 255, 255, 0.01)' : 'transparent',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        boxShadow: 'none',
        transition: 'all 0.3s ease-in-out',
        py: 1,
        zIndex: theme => theme.zIndex.drawer + 1,
      }}>
      <Container maxWidth='xl'>
        <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
          <Stack alignItems={'center'} justifyContent={'center'}>
            <Link href='/' style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
              <Box sx={{ position: 'relative', width: 50, height: 50 }}>
                <Image src='/logo.png' alt='LFG Logo' fill style={{ objectFit: 'contain' }} priority />
              </Box>
            </Link>
          </Stack>

          <Stack direction={'row'} gap={4} alignItems={'center'}>
            <Stack gap={4} direction={'row'} alignItems={'center'} display={{ xs: 'none', md: 'flex' }}>
              {renderNav()}
            </Stack>
            <Stack direction={'row'} alignItems={'center'} gap={2}>
              <Stack display={{ xs: 'none', md: 'flex' }}>
                <HexagonalButton
                  variant={'primary'}
                  width={142}
                  label={'LAUNCH APP'}
                  labelClassName={'!text-[24px]'}
                  onClick={openAdminPage}
                />
              </Stack>
              <Stack display={{ xs: 'flex', md: 'none' }}>
                <HexagonalButton
                  variant={'primary'}
                  width={140}
                  size={'small'}
                  label={'LAUNCH APP'}
                  labelClassName={'!text-[24px]'}
                  onClick={openAdminPage}
                />
              </Stack>

              <MobileMenu>
                <Stack gap={2} padding={3}>
                  {renderNav()}
                </Stack>
              </MobileMenu>
            </Stack>
          </Stack>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

interface MobileMenuProps {
  children: React.ReactNode;
}

const MobileMenu = ({ children }: MobileMenuProps) => {
  const [initialPadding, setInitialPadding] = useState<string>('');
  const [isDrawerOpened, setIsDrawerOpened] = useState<boolean>(false);

  const toggle = () => setIsDrawerOpened(prev => !prev);
  const handleClose = () => setIsDrawerOpened(false);

  useEffect(() => {
    if (isDrawerOpened) {
      setInitialPadding(document.body.style.paddingRight);
      const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;

      document.body.style.paddingRight = `${scrollBarWidth}px`;

      document.body.style.overflow = 'hidden';
      document.body.style.height = '100%';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';

      return () => {
        document.body.style.overflow = '';
        document.body.style.height = '';
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.paddingRight = initialPadding;
      };
    }
  }, [isDrawerOpened, initialPadding]);

  return (
    <Stack sx={{ display: { xs: 'contents', md: 'none' } }}>
      <IconButton onClick={toggle} sx={{ padding: 0 }}>
        {isDrawerOpened ? (
          <CloseIcon fontSize={'medium'} sx={{ color: '#FFF' }} />
        ) : (
          <MenuIcon fontSize={'medium'} sx={{ color: '#FFF' }} />
        )}
      </IconButton>
      <Drawer
        anchor={'top'}
        hideBackdrop
        variant={'temporary'}
        open={isDrawerOpened}
        onClose={() => setIsDrawerOpened(false)}
        sx={{ zIndex: 100 }}
        PaperProps={{
          sx: {
            height: '100dvh',
            paddingTop: '72px',
            backgroundColor: '#060912',
            boxShadow: 'none !important',
          },
        }}>
        {cloneElement(children as React.ReactElement<any>, {
          onClick: handleClose,
        })}
      </Drawer>
    </Stack>
  );
};

export default Header;
